import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { completeRedirect, OneDriveAuthError } from './oneDriveAuth';

/**
 * Where Microsoft sends the user back after a redirect sign-in.
 *
 * Like googleRedirect and unlike the old dropboxRedirect, this is not a popup
 * talking to an opener - it is the app itself, mid-navigation, and it is the
 * only page loaded at this point. MSAL reads the response out of the URL,
 * caches the tokens, and then we put the user back where they were.
 *
 * There is no token to carry across that navigation: MSAL's cache is in
 * localStorage, so OneDriveService finds the account for itself when it mounts
 * on the other side. Only the intent - "and make this the storage provider" -
 * has to survive, and oneDriveAuth stashes that.
 *
 * It deliberately renders almost nothing on the happy path. Anything heavier
 * would be a second thing that can fail between the user having a valid token
 * and the app knowing about it.
 */
export default function OneDriveRedirect() {

  const history = useHistory();
  const [error, setError] = useState<string>();
  const [returnTo, setReturnTo] = useState('/app');

  // The work is a one-shot side effect; a re-run would find the response
  // already consumed and report a spurious failure.
  const handled = useRef(false);

  useEffect(function completeOneDriveSignIn() {
    if (handled.current) {
      return;
    }

    handled.current = true;

    completeRedirect().then(
      function returnToApp(result) {
        // replace, not push: the URL still carries the authorisation response,
        // and there is no reason to leave that in history or to let Back land
        // on a callback that can no longer be completed.
        history.replace(result.returnTo);
      },
      function reportFailure(err: OneDriveAuthError) {
        setReturnTo(err.returnTo || '/app');
        setError(err.message || 'OneDrive sign-in failed.');
      },
    );
  }, [history]);

  if (!error) {
    return <p>Completing OneDrive sign-in…</p>;
  }

  return (
    <article className="mainViewport">
      <section>
        <h5>OneDrive sign-in did not complete</h5>
        <p>{error}</p>
        <button
          className="button"
          onClick={() => history.replace(returnTo)}
        >
          Back to Fadila
        </button>
      </section>
    </article>
  );
}
